//----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// WineProfilePage - Wine Preference Profile
//----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
import React from 'react';
import { withRouter } from './withRouter';
import { SessionInfo } from './App';
import { Button } from '@progress/kendo-react-buttons';
import { Popup } from '@progress/kendo-react-popup';
import { TreeView } from '@progress/kendo-react-treeview';
import { Input, Slider, SliderLabel, Switch } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { CallOM, CTYP, trace } from './CommonCode.js';

import { ReactComponent as MenuBar } from "./images/bars.svg";

// Dropdown option arrays
const WineExperienceIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Total beginner' }, { id: 2, text: 'I know a little' }, { id: 3, text: 'Comfortable choosing' }, { id: 4, text: 'Very knowledgeable' }, { id: 5, text: 'Sommelier-level' }];
const WineChoiceIDDD = [{ id: 0,text: '' },{ id: 1,text: 'Red' },{ id: 2,text: 'White' }, { id: 3,text: 'White and Red' }, { id: 4, text: 'Rosé' }, { id: 5, text: 'Sparkling' }, { id: 6, text: 'Open to anything' }];
const DrynessIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Bone-dry' }, { id: 2, text: 'Dry' }, { id: 3, text: 'Off-dry (a touch of sweetness)' }, { id: 4, text: 'Semi-sweet' }, { id: 5, text: 'Sweet' }];
const AcidityIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Low (soft, round)' }, { id: 2, text: 'Medium' }, { id: 3, text: 'High (bright, crisp, zesty)' }];
const TanninIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Low (smooth, easy-drinking)' }, { id: 2, text: 'Medium' }, { id: 3, text: 'High (firm, structured, grippy)' }];
const FoodPairingIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Red meat / game' }, { id: 2, text: 'Poultry / pork' }, { id: 3, text: 'Seafood / fish' }, { id: 4, text: 'Pasta / pizza' }, { id: 5, text: 'Salads / vegetables' }, { id: 6, text: 'Cheese / charcuterie' }, { id: 7, text: 'Spicy cuisine (Thai, Indian, Mexican)' }, { id: 8, text: 'Desserts' }, { id: 9, text: 'No food — just sipping' }];
const SpicyLevelIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Mild / not spicy' }, { id: 2, text: 'Medium heat' }, { id: 3, text: 'I love it hot' }];
const WineBudgetIDDD = [{ id: 0,text: '' },{ id: 1,text: 'Under $50' },{ id: 2,text: '$40–$90' },{ id: 3,text: '$80–$120' }, { id: 4, text: 'Over $110 — treat me' }];
const VarietyOpenIDDD = [{ id: 0, text: '' }, { id: 1, text: 'Stick to what I know' }, { id: 2, text: 'Mostly familiar with occasional surprises' }, { id: 3, text: 'Love discovering new wines' }, { id: 4, text: 'The weirder the better' }];

//------ Encoded Values - WhiteWineFlavors ------
const WhiteWineFlavorsFixedValues = [{ n: 'wfCitrus', k: 1 }, { n: 'wfStoneFruit', k: 2 }, { n: 'wfTropical', k: 3 }, { n: 'wfGreenApple', k: 4 }, { n: 'wfFloralHerbal', k: 5 }, { n: 'wfButteryOaky', k: 6 }];
//------ Encoded Values - RedWineFlavors ------
const RedWineFlavorsFixedValues = [{ n: 'rfRedFruit', k: 1 }, { n: 'rfDarkFruit', k: 2 }, { n: 'rfEarthy', k: 3 }, { n: 'rfSpicy', k: 4 }, { n: 'rfSmoky', k: 5 }, { n: 'rfHerbaceous', k: 6 }];

class WineProfilePage extends React.Component {
  constructor(props) {
    super(props);
    if (!SessionInfo.session || SessionInfo.session === '')
      return;
  }
  menuAnchor = React.createRef();
  state = {
    // Section 1: WineExperienceID & Confidence
    WineExperienceID: 0,
    WineChoiceID: 0,
    // Section 2: Taste Preferences — Style
    BodyStyleID: 3,
    DrynessID: 0,
    AcidityID: 0,
    TanninID: 0,
    // Section 3: Taste Preferences — Flavors
    WhiteWineFlavors: 0,
    //------ Encoded Bit Values For WhiteWineFlavors -------
    wfCitrus: false,
    wfStoneFruit: false,
    wfTropical: false,
    wfGreenApple: false,
    wfFloralHerbal: false,
    wfButteryOaky: false,
    RedWineFlavors: 0,
    //------ Encoded Bit Values For RedWineFlavors -------
    rfRedFruit: false,
    rfDarkFruit: false,
    rfEarthy: false,
    rfSpicy: false,
    rfSmoky: false,
    rfHerbaceous: false,
    FlavorNotes: '',
    // Section 4: Food Context
    FoodPairingID: 0,
    SpicyLevelID: 0,
    FoodNotes: '',
    // Section 5: Practical Constraints
    WineBudgetID: 0,
    VarietyOpenID: 0,
    // Control
    showMenu: false,
    NotifyMessage: '',
    NotifyIsError: false,
  };
  compUpdate = true;
  async componentDidMount() {
    if (!SessionInfo.session || SessionInfo.session === '')
      return;
    SessionInfo.currentPage = "WineProfilePage";
    this.setState({ NotifyMessage: '' });
    let CD = await CallOM('GetWineProfile', 0, 0);
    if (CD.x.o < 9500) {
      let row = CD.d;
      if (row) {
        let stateUpdate = {};
        Object.keys(row).forEach(key => {
          if (key in this.state) {
            stateUpdate[key] = row[key];
          }
        });
        if (Object.keys(stateUpdate).length > 0)
          this.setState(stateUpdate);
        if (row.WhiteWineFlavors !== undefined)
          this.setWhiteWineFlavorsBitEncoded(row.WhiteWineFlavors);
        if (row.RedWineFlavors !== undefined)
          this.setRedWineFlavorsBitEncoded(row.RedWineFlavors);
      }
    }
    // TEST - remove after verifying
    //  this.notify('Wine profile saved successfully!');
    //  setTimeout(() => this.notify('Error saving wine profile', true), 6000);
  }
  shouldComponentUpdate() {
    if (this.compUpdate === true)
      return true;
    else
      return false;
  }
  gotoLanding = async () => {
    trace("Go To LandingPage");
    this.props.navigate("/LandingPage");
  }
  openMenu = (event) => {
    event.stopPropagation();
    this.setState({ showMenu: true });
  }
  onMenuItemClick = async (event) => {
    trace(`menu event Item: ${event.item.text}`);
    if (SessionInfo.currentItem !== undefined) {
      SessionInfo.currentItem.selected = false;
    }
    SessionInfo.currentItem = event.item;
    event.item.selected = true;
    event.item.expanded = true;
    SessionInfo.MenuSelect = event.item;
    this.setState({ showMenu: false });
    if (SessionInfo.menuAction !== undefined) {
      await SessionInfo.menuAction();
    }
    this.forceUpdate();
  }
  closePopups = () => {
    this.setState({ showMenu: false });
  }
  chgFldVal = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  }
  onDropChange = (field, event) => {
    this.setState({ [field]: event.target.value });
  }
  onDropChangeInt = (field, event) => {
    this.setState({ [field]: event.target.value.id });
  }
  chgCheckboxVal = (event) => {
    let setVal = true;
    if (this.state[event.target.name] === true)
      setVal = false;
    this.setState({ [event.target.name]: setVal });
  }
  onSliderChange = (event) => {
    this.setState({ BodyStyleID: event.value });
  }
  notifyTimeout = null;
  notify = (msg, isError = false) => {
    if (this.notifyTimeout)
      clearTimeout(this.notifyTimeout);
    this.setState({ NotifyMessage: msg, NotifyIsError: isError });
    this.notifyTimeout = setTimeout(() => {
      this.setState({ NotifyMessage: '' });
      this.notifyTimeout = null;
    }, 4000);
  }
  saveWineProfile = async () => {
    await this.getWhiteWineFlavorsBitEncoded();
    await this.getRedWineFlavorsBitEncoded();
    const profile = {
      WineExperienceID: this.state.WineExperienceID,
      WineChoiceID: this.state.WineChoiceID,
      BodyStyleID: this.state.BodyStyleID,
      DrynessID: this.state.DrynessID,
      AcidityID: this.state.AcidityID,
      TanninID: this.state.TanninID,
      WhiteWineFlavors: this.state.WhiteWineFlavors,
      RedWineFlavors: this.state.RedWineFlavors,
      FlavorNotes: this.state.FlavorNotes,
      FoodPairingID: this.state.FoodPairingID,
      SpicyLevelID: this.state.SpicyLevelID,
      FoodNotes: this.state.FoodNotes,
      WineBudgetID: this.state.WineBudgetID,
      VarietyOpenID: this.state.VarietyOpenID,
    };
    trace(`Save Wine Profile: ${JSON.stringify(profile)}`);
    let CD = await CallOM('SaveWineProfile', 0, 0, CTYP.DICTNO, profile);
    if (CD.x.o < 9500)
      this.notify('Wine profile saved successfully!');
    else
      this.notify('Error saving wine profile', true);
  }
  clearWineProfile = () => {
    if (this.notifyTimeout)
      clearTimeout(this.notifyTimeout);
    this.setState({
      WineExperienceID: 0,
      WineChoiceID: 0,
      BodyStyleID: 3,
      DrynessID: 0,
      AcidityID: 0,
      TanninID: 0,
      WhiteWineFlavors: 0,
      wfCitrus: false,
      wfStoneFruit: false,
      wfTropical: false,
      wfGreenApple: false,
      wfFloralHerbal: false,
      wfButteryOaky: false,
      RedWineFlavors: 0,
      rfRedFruit: false,
      rfDarkFruit: false,
      rfEarthy: false,
      rfSpicy: false,
      rfSmoky: false,
      rfHerbaceous: false,
      FlavorNotes: '',
      FoodPairingID: 0,
      SpicyLevelID: 0,
      FoodNotes: '',
      WineBudgetID: 0,
      VarietyOpenID: 0,
      NotifyMessage: '',
      NotifyIsError: false,
    });
  }
  // Convert Encoded Bit-Values For WhiteWineFlavors
  getWhiteWineFlavorsBitEncoded = async () => {
    let WhiteWineFlavors = 0x0000000000000000;
    if (this.state.wfCitrus === true)
      WhiteWineFlavors |= (0x001 << 1);
    if (this.state.wfStoneFruit === true)
      WhiteWineFlavors |= (0x001 << 2);
    if (this.state.wfTropical === true)
      WhiteWineFlavors |= (0x001 << 3);
    if (this.state.wfGreenApple === true)
      WhiteWineFlavors |= (0x001 << 4);
    if (this.state.wfFloralHerbal === true)
      WhiteWineFlavors |= (0x001 << 5);
    if (this.state.wfButteryOaky === true)
      WhiteWineFlavors |= (0x001 << 6);
    await this.setState({ WhiteWineFlavors });
  }
  // Set State Variables for Encoded Bit-Values For WhiteWineFlavors
  setWhiteWineFlavorsBitEncoded = (WhiteWineFlavorsVals) => {
    WhiteWineFlavorsFixedValues.forEach((ent) => {
      if ((WhiteWineFlavorsVals & (0x001 << ent.k)) === (0x001 << ent.k))
        this.setState({ [ent.n]: true });
      else
        this.setState({ [ent.n]: false });
    });
  }
  // Convert Encoded Bit-Values For RedWineFlavors
  getRedWineFlavorsBitEncoded = async () => {
    let RedWineFlavors = 0x0000000000000000;
    if (this.state.rfRedFruit === true)
      RedWineFlavors |= (0x001 << 1);
    if (this.state.rfDarkFruit === true)
      RedWineFlavors |= (0x001 << 2);
    if (this.state.rfEarthy === true)
      RedWineFlavors |= (0x001 << 3);
    if (this.state.rfSpicy === true)
      RedWineFlavors |= (0x001 << 4);
    if (this.state.rfSmoky === true)
      RedWineFlavors |= (0x001 << 5);
    if (this.state.rfHerbaceous === true)
      RedWineFlavors |= (0x001 << 6);
    await this.setState({ RedWineFlavors });
  }
  // Set State Variables for Encoded Bit-Values For RedWineFlavors
  setRedWineFlavorsBitEncoded = (RedWineFlavorsVals) => {
    RedWineFlavorsFixedValues.forEach((ent) => {
      if ((RedWineFlavorsVals & (0x001 << ent.k)) === (0x001 << ent.k))
        this.setState({ [ent.n]: true });
      else
        this.setState({ [ent.n]: false });
    });
  }
  //----------------------------------------------------------------------------------------------------------------------------------
  render() {
    return (
      <div id="WineProfilePage" className="pageMain">

        <div id="hdr" className="header" style={{ position: 'fixed', top: 0, left: 0, zIndex: 5, maxWidth: '750px', minWidth: '350px' }} onClick={(e) => this.closePopups(e)}>
          <div className="headerLogoLeft">
            {SessionInfo.HasLandingPage ?
              <a id="headerLogo" style={{ backgroundImage: `url(${SessionInfo.LogoImageURL})`,backgroundPosition: 'left top',backgroundRepeat: 'no-repeat',backgroundSize: 'cover',marginLeft: '8px',marginTop: '5px' }} onClick={(e) => this.gotoLanding()} />
              :
              <a id="headerLogo" style={{ backgroundImage: `url(${SessionInfo.LogoImageURL})`,backgroundPosition: 'left top',backgroundRepeat: 'no-repeat',backgroundSize: 'cover',marginLeft: '8px',marginTop: '5px' }} href={SessionInfo.RestaurantURL} target="_blank" rel="noopener noreferrer" />}
          </div>
          <div className="headerCenter">
            <div className="headerCenterMenu">
              <MenuBar alt="" fill={SessionInfo.headingTextColor} id="MenuBtn" title={`View Menu`} className="functionImageL" onClick={(e) => this.openMenu(e)} ref={this.menuAnchor} />
            </div>
            <div className="headerCenterTop">
              <span className='landingHeadTextLarge'>&nbsp; &nbsp; &nbsp; Wine Profile</span>
              <br />
              {/* Notification */}
              {this.state.NotifyMessage !== '' ?
                <div style={{ padding: '6px 12px', fontSize: '14px', fontWeight: '500', color: this.state.NotifyIsError ? '#d32f2f' : '#2e7d32', backgroundColor: this.state.NotifyIsError ? '#fdecea' : '#edf7ed', border: this.state.NotifyIsError ? '1px solid #f5c6cb' : '1px solid #c3e6cb', borderRadius: '6px', textAlign: 'center', marginTop: '4px' }}>
                  {this.state.NotifyMessage}
                </div>
                : 
                <div style={{ marginTop: '40px', marginBottom: '5px', textAlign: 'center' }}>
                  <Button icon="save" look="outline" className="largeButton" onClick={this.saveWineProfile} style={{ borderColor: 'white' }}>&nbsp;Save Profile&nbsp;</Button>
                  &nbsp;&nbsp;
                  <Button icon="reset" look="outline" className="largeButton" onClick={this.clearWineProfile} style={{ borderColor: 'white' }}>&nbsp;Clear&nbsp;</Button>
                </div>}
            </div>
          </div>
        </div>

        <Popup anchor={this.menuAnchor.current} show={this.state.showMenu} popupClass={'popupMenu'} >
          <div className="mainMenu">
            <a id="headerLogo" style={{ backgroundImage: `url(${SessionInfo.LogoImageURL})`, backgroundPosition: 'left top', backgroundRepeat: 'no-repeat', backgroundSize: '100px 60px' }} href="http://www.omnovos.com" target="_blank" rel="noopener noreferrer" />
            <div className="menuTree">
              {SessionInfo.AppCfg.HasScanAndPay === false ?
                <br /> : null}
              {SessionInfo.defaultLogon === true ?
                <br /> : null}
              <TreeView
                data={SessionInfo.MenuControlNQR}
                size='large' draggable='false' selection='single'
                onItemClick={this.onMenuItemClick}
                className='menuTreeSub'
                item={props => [<span key={props.item.key} className={props.item.className}>{props.item.icon}{props.item.space}{props.item.text}</span>]}
              />
            </div>
          </div>
        </Popup>

        <div className="contactEdit" style={{ height: 'calc(var(--screen-height)+10px)', overflowY: 'auto', top: '108px', position: 'relative' }}>
          <div className="contactBox" style={{ height: 'auto', overflow: 'visible' }}>

            {/* ===== Section 1: Wine Experience & Confidence ===== */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '16px', fontWeight: 'bold', color: SessionInfo.activeColor }}>Wine Experience &amp; Confidence</label>
            </div>
            <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
              <span className="editFieldLabel">Wine Knowledge</span>
              <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                <DropDownList id="WineExperienceID" data={WineExperienceIDDD} textField="text" dataItemKey="id" value={WineExperienceIDDD.find(o => o.id === this.state.WineExperienceID)} onChange={(e) => this.onDropChangeInt('WineExperienceID', e)} className="editInputDD" style={{ position: 'relative' }} />
              </div>
            </div>
            <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
              <span className="editFieldLabel">Prefer Red, White, or Other?</span>
              <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                <DropDownList id="WineChoiceID" data={WineChoiceIDDD} textField="text" dataItemKey="id" value={WineChoiceIDDD.find(o => o.id === this.state.WineChoiceID)} onChange={(e) => this.onDropChangeInt('WineChoiceID', e)} className="editInputDD" style={{ position: 'relative' }} />
              </div>
            </div>

            {/* ===== Section 2: Taste Preferences — Style ===== */}
            <div style={{ marginTop: '15px', marginBottom: '10px' }}>
              <label style={{ fontSize: '16px', fontWeight: 'bold', color: SessionInfo.activeColor }}>Taste Preferences — Style</label>
            </div>
            <div className="editFieldW" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '10px' }}>
              <label style={{ fontSize: '14px', marginBottom: '5px' }}>Body / Weight preference:</label>
              <Slider min={1} max={5} step={1} value={this.state.BodyStyleID} onChange={this.onSliderChange} style={{ width: '90%', color: SessionInfo.inputTextColor }}>
                <SliderLabel position={1}>Light</SliderLabel>
                <SliderLabel position={3}>Medium</SliderLabel>
                <SliderLabel position={5}>Bold</SliderLabel>
              </Slider>
            </div>
            {this.state.WineExperienceID > 3 ?
              <>
              <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
                <span className="editFieldLabel">Sweetness / Dryness</span>
                <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                  <DropDownList id="DrynessID" data={DrynessIDDD} textField="text" dataItemKey="id" value={DrynessIDDD.find(o => o.id === this.state.DrynessID)} onChange={(e) => this.onDropChangeInt('DrynessID', e)} className="editInputDD" style={{ position: 'relative' }} />
                </div>
              </div>
              <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
                <span className="editFieldLabel">Acidity Preference</span>
                <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                  <DropDownList id="AcidityID" data={AcidityIDDD} textField="text" dataItemKey="id" value={AcidityIDDD.find(o => o.id === this.state.AcidityID)} onChange={(e) => this.onDropChangeInt('AcidityID', e)} className="editInputDD" style={{ position: 'relative' }} />
                </div>
              </div>
              <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
                <span className="editFieldLabel">Tannin Preference (Reds)</span>
                <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                  <DropDownList id="TanninID" data={TanninIDDD} textField="text" dataItemKey="id" value={TanninIDDD.find(o => o.id === this.state.TanninID)} onChange={(e) => this.onDropChangeInt('TanninID', e)} className="editInputDD" style={{ position: 'relative' }} />
                </div>
              </div>
            </> : null}
            {/* ===== Section 3: Taste Preferences — Flavors ===== */}
            <div style={{ marginTop: '15px', marginBottom: '10px' }}>
              <label style={{ fontSize: '16px', fontWeight: 'bold', color: SessionInfo.activeColor }}>Taste Preferences — Flavors</label>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span className="editFieldLabel" style={{ fontWeight: 'bold' }}>White Wine Flavors</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.wfCitrus} name="wfCitrus" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Citrus (lemon, lime, grapefruit)</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.wfStoneFruit} name="wfStoneFruit" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Stone fruit (peach, apricot)</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.wfTropical} name="wfTropical" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Tropical (mango, pineapple)</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.wfGreenApple} name="wfGreenApple" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Green apple / pear</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.wfFloralHerbal} name="wfFloralHerbal" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Floral / herbal</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.wfButteryOaky} name="wfButteryOaky" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Buttery / oaky / vanilla</span>
            </div>
            <div style={{ marginTop: '10px', marginBottom: '8px' }}>
              <span className="editFieldLabel" style={{ fontWeight: 'bold' }}>Red Wine Flavors</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.rfRedFruit} name="rfRedFruit" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Red fruit (cherry, strawberry, raspberry)</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.rfDarkFruit} name="rfDarkFruit" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Dark fruit (blackberry, plum, cassis)</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.rfEarthy} name="rfEarthy" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Earthy / mushroom / forest floor</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.rfSpicy} name="rfSpicy" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Spicy (pepper, clove, cinnamon)</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.rfSmoky} name="rfSmoky" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Smoky / chocolatey / coffee</span>
            </div>
            <div className="checkboxRow">
              <input type="checkbox" className="checkboxInput" checked={this.state.rfHerbaceous} name="rfHerbaceous" onChange={evt => this.chgCheckboxVal(evt)} /><span className="editFieldLabel">Herbaceous (green pepper, dried herbs)</span>
            </div>
            <div className="editFieldW" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '5px', marginTop: '15px' }}>
              <label style={{ fontSize: '14px' }}>Any specific wines or grapes you love (or hate)?</label>
              <textarea name="FlavorNotes" rows="3" style={{ width: '97%', padding: '5px', fontSize: '14px', resize: 'none', borderRadius: '10px', border: '1px solid #ccc' }} value={this.state.FlavorNotes} onChange={(e) => this.chgFldVal(e)} />
            </div>

            {/* ===== Section 4: Food Context ===== */}
            <div style={{ marginTop: '15px', marginBottom: '10px' }}>
              <label style={{ fontSize: '16px', fontWeight: 'bold', color: SessionInfo.activeColor }}>Food Context</label>
            </div>
            <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
              <span className="editFieldLabel">Food Pairing</span>
              <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                <DropDownList id="FoodPairingID" data={FoodPairingIDDD} textField="text" dataItemKey="id" value={FoodPairingIDDD.find(o => o.id === this.state.FoodPairingID)} onChange={(e) => this.onDropChangeInt('FoodPairingID', e)} className="editInputDD" style={{ position: 'relative' }} />
              </div>
            </div>
            <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
              <span className="editFieldLabel">Spicy Food Tolerance</span>
              <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                <DropDownList id="SpicyLevelID" data={SpicyLevelIDDD} textField="text" dataItemKey="id" value={SpicyLevelIDDD.find(o => o.id === this.state.SpicyLevelID)} onChange={(e) => this.onDropChangeInt('SpicyLevelID', e)} className="editInputDD" style={{ position: 'relative' }} />
              </div>
            </div>
            <div className="editFieldW" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '5px' }}>
              <label style={{ fontSize: '14px' }}>Any dietary restrictions or food notes?</label>
              <textarea name="FoodNotes" rows="3" style={{ width: '97%', padding: '5px', fontSize: '14px', resize: 'none', borderRadius: '10px', border: '1px solid #ccc' }} value={this.state.FoodNotes} onChange={(e) => this.chgFldVal(e)} />
            </div>

            {/* ===== Section 5: Practical Constraints ===== */}
            <div style={{ marginTop: '15px', marginBottom: '10px' }}>
              <label style={{ fontSize: '16px', fontWeight: 'bold', color: SessionInfo.activeColor }}>Practical Constraints</label>
            </div>
            <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
              <span className="editFieldLabel">Budget Per Bottle</span>
              <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                <DropDownList id="WineBudgetID" data={WineBudgetIDDD} textField="text" dataItemKey="id" value={WineBudgetIDDD.find(o => o.id === this.state.WineBudgetID)} onChange={(e) => this.onDropChangeInt('WineBudgetID', e)} className="editInputDD" style={{ position: 'relative' }} />
              </div>
            </div>
            <div className="editField" style={{ height: 'auto', paddingTop: '4px', paddingBottom: '8px' }}>
              <span className="editFieldLabel">Openness to New Wines</span>
              <div className="editDDPos" style={{ position: 'relative', left: '7px', marginTop: '3px' }}>
                <DropDownList id="VarietyOpenID" data={VarietyOpenIDDD} textField="text" dataItemKey="id" value={VarietyOpenIDDD.find(o => o.id === this.state.VarietyOpenID)} onChange={(e) => this.onDropChangeInt('VarietyOpenID', e)} className="editInputDD" style={{ position: 'relative' }} />
              </div>
            </div>

            {/* ===== Buttons ===== */}
            <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center' }}>
              <Button icon="save" look="outline" className="largeButton" onClick={this.saveWineProfile} style={{ borderColor: SessionInfo.ECV.BrandColor }}>&nbsp;Save Profile&nbsp;</Button>
              &nbsp;&nbsp;
              <Button icon="reset" look="outline" className="largeButton" onClick={this.clearWineProfile} style={{ borderColor: SessionInfo.ECV.BrandColor }}>&nbsp;Clear&nbsp;</Button>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(WineProfilePage);
